import React, { useState, useEffect } from 'react';
import { Search, UserCheck, UserX, Eye, Mail, Phone } from 'lucide-react';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setTimeout(() => {
      setUsers([
        {
          id: 1,
          name: 'Jana Svobodová',
          email: 'jana.svobodova@email.cz',
          phone: '+420 123 456 789',
          role: 'customer',
          status: 'active',
          joinDate: '2024-11-15',
          lastLogin: '2025-01-09',
          totalBookings: 12,
          totalSpent: 4580.50
        },
        {
          id: 2,
          name: 'Petr Novák',
          email: 'petr.novak@email.cz',
          phone: '+420 987 654 321',
          role: 'customer',
          status: 'active',
          joinDate: '2024-12-03',
          lastLogin: '2025-01-08',
          totalBookings: 8,
          totalSpent: 2340.00
        },
        {
          id: 3,
          name: 'Marie Černá',
          email: 'marie.cerna@email.cz',
          phone: '+420 555 123 456',
          role: 'provider',
          status: 'suspended',
          joinDate: '2024-10-20',
          lastLogin: '2025-01-05',
          totalBookings: 156,
          totalSpent: 0,
          suspensionReason: 'Multiple customer complaints'
        },
        {
          id: 4,
          name: 'Tomáš Dvořák',
          email: 'tomas.dvorak@email.cz',
          phone: '+420 777 888 999',
          role: 'provider',
          status: 'active',
          joinDate: '2024-09-12',
          lastLogin: '2025-01-09',
          totalBookings: 234,
          totalSpent: 0
        }
      ]);
      setLoading(false);
    }, 1000);
  };

  const handleStatusChange = async (userId, newStatus) => {
    setUsers(users.map(user =>
      user.id === userId
        ? { ...user, status: newStatus }
        : user
    ));
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const StatusBadge = ({ status }) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const RoleBadge = ({ role }) => {
    const colors = {
      customer: 'bg-blue-100 text-blue-800',
      provider: 'bg-purple-100 text-purple-800',
      admin: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[role]}`}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-1">Manage customer and provider accounts</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          <div className="text-sm text-gray-500">
            {users.filter(u => u.role === 'customer').length} customers
          </div>
          <div className="text-sm text-gray-500">
            {users.filter(u => u.role === 'provider').length} providers
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Roles</option>
              <option value="customer">Customers</option>
              <option value="provider">Providers</option>
              <option value="admin">Admins</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-900">User</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Role</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Join Date</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Activity</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-3">
                          <div className="font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                          <div className="text-sm text-gray-500">{user.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <RoleBadge role={user.role} />
                    </td>
                    <td className="py-4 px-4">
                      <StatusBadge status={user.status} />
                      {user.suspensionReason && (
                        <div className="text-xs text-red-600 mt-1">
                          {user.suspensionReason}
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-sm text-gray-900">{user.joinDate}</div>
                      <div className="text-xs text-gray-500">Last: {user.lastLogin}</div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-sm text-gray-900">
                        {user.totalBookings} bookings
                      </div>
                      {user.role === 'customer' && (
                        <div className="text-xs text-gray-500">
                          {user.totalSpent.toLocaleString()} Kč spent
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <button
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title="View Profile"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                          title="Send Email"
                        >
                          <Mail className="h-4 w-4" />
                        </button>
                        <button
                          className="p-1 text-purple-600 hover:bg-purple-50 rounded"
                          title="Call User"
                        >
                          <Phone className="h-4 w-4" />
                        </button>
                        {user.status === 'active' ? (
                          <button
                            onClick={() => handleStatusChange(user.id, 'suspended')}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Suspend User"
                          >
                            <UserX className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleStatusChange(user.id, 'active')}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                            title="Activate User"
                          >
                            <UserCheck className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;